import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import TableModal from '../../../../../common/Modal/TableModal/TableModal';

const LoadAudienceModal = ({
  data = [],
  onSubmit,
  onCancel,
  onDelete,
  isOpen = false,
  channel,
  isActivateAudienceVisible = false,
  userPermissions = [],
}) => {
  // Check if user has specific permission
  const hasPermission = (permission) => userPermissions.includes(permission);

  // Transform data to add display fields
  const transformedData = useMemo(() => {
    const filteredData = channel
      ? data.filter((audience) => audience.channel === channel || (channel === 'articles' && !audience.channel))
      : data;
    return filteredData.map((audience) => ({
      ...audience,
      createdBy: audience.created_by,
      dateActivated: audience.activation?.deterministic?.activated_date,
      dateAmplified: audience.activation?.amplified?.activated_date,
      dateAlwaysOn: audience.activation?.always_on?.activated_date,
      createdAt: audience.created_at,
    }));
  }, [data, channel]);

  const handleDelete = (audience, event) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(audience);
    }
  };

  const DeleteButton = ({ row }) => (
    <span className="action-btn delete-btn" onClick={(e) => handleDelete(row, e)} title="Delete">
      <i className="icon-trash pointer"></i>
    </span>
  );

  DeleteButton.displayName = 'DeleteButton';
  DeleteButton.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const renderDeleteButton = (row) => <DeleteButton row={row} />;
  renderDeleteButton.displayName = 'renderDeleteButton';

  // Dynamic column configuration based on original Angular logic
  const columns = useMemo(() => {
    // Base columns (always shown)
    const baseColumns = [
      {
        id: 'name',
        title: 'Audience Name',
        sortable: true,
        className: 'cell-name',
        showTooltip: true,
        flex: 2, // Wider for names
      },
      {
        id: 'createdBy',
        title: 'Created By',
        sortable: true,
        className: 'cell-name',
        showTooltip: true,
        flex: 1,
      },
      {
        id: 'createdAt',
        title: 'Date Added',
        sortable: true,
        className: 'cell-date',
        flex: 1,
      },
    ];

    // Build activation fields based on original logic
    let activationFields = [];

    if (isActivateAudienceVisible) {
      // Always include date_amplified
      activationFields.push({
        id: 'dateAmplified',
        title: 'Date Amplified',
        sortable: true,
        className: 'cell-date',
        flex: 1,
      });

      // Add date_always_on if user has permission
      if (hasPermission('audience activation - always on')) {
        activationFields.push({
          id: 'dateAlwaysOn',
          title: 'Date Always On Created',
          sortable: true,
          className: 'cell-date',
          flex: 1,
        });
      }

      // Add date_activated at the beginning if channel !== 'articles'
      if (channel !== 'articles') {
        activationFields = [
          {
            id: 'dateActivated',
            title: 'Date Activated',
            sortable: true,
            className: 'cell-date',
            flex: 1,
          },
          ...activationFields,
        ];
      }
    }

    // Actions column (always at the end)
    const actionsColumn = {
      id: 'actions',
      title: '',
      sortable: false,
      className: 'cell-actions',
      flex: 0.3, // Smaller for actions
      template: renderDeleteButton,
    };

    // Combine all columns
    return [...baseColumns, ...activationFields, actionsColumn];
  }, [channel, isActivateAudienceVisible, userPermissions]);

  const handleRowClick = (audience) => {
    if (onSubmit) {
      onSubmit(audience);
    }
  };

  const width = useMemo(() => {
    let widthAsNum = 700;
    if (isActivateAudienceVisible) {
      widthAsNum += 100;
      if (channel !== 'articles') {
        widthAsNum += 100;
      }
      if (hasPermission('audience activation - always on')) {
        widthAsNum += 200;
      }
    }
    return `${widthAsNum}px`;
  }, [channel, isActivateAudienceVisible, userPermissions]);

  return (
    <TableModal
      isOpen={isOpen}
      modalTitle="Load a Program Audience"
      subtitle="Select an audience by clicking its row"
      data={transformedData}
      columns={columns}
      onRowClick={handleRowClick}
      onCancel={onCancel}
      searchPlaceholder="Search audiences..."
      emptyMessage="No audiences found"
      searchableFields={['name', 'createdBy']}
      defaultSort={{ key: 'createdAt', direction: 'desc' }}
      className="load-audience-modal"
      width={width}
      height="527px"
    />
  );
};

LoadAudienceModal.propTypes = {
  data: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  isOpen: PropTypes.bool,
  channel: PropTypes.string,
  isActivateAudienceVisible: PropTypes.bool,
  userPermissions: PropTypes.array,
};

export default LoadAudienceModal;
